import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import type { ReactNode } from "react";
import { Shell } from "./pages/Shell.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Sources } from "./pages/Sources.js";
import { Items } from "./pages/Items.js";
import { ItemDetail } from "./pages/ItemDetail.js";
import { Entities } from "./pages/Entities.js";
import { Alerts } from "./pages/Alerts.js";
import { ModuleSeed } from "./pages/ModuleSeed.js";
import { CommandCenter } from "./pages/CommandCenter.js";
import { Overview } from "./pages/Overview.js";
import { IntelPage } from "./pages/IntelPage.js";
import { SearchPage } from "./pages/SearchPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";

interface AppProps {
  devModeNoAuth?: boolean;
}

export function App({ devModeNoAuth }: AppProps) {
  if (devModeNoAuth) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route element={<Shell devModeNoAuth />}>
          <Route path="/app" element={<Overview devModeNoAuth />} />
          <Route path="/app/admin/seed/:module" element={<ModuleSeed devModeNoAuth />} />
          <Route path="/app/command" element={<CommandCenter devModeNoAuth />} />
          <Route path="/app/intel" element={<IntelPage devModeNoAuth />} />
          <Route path="/app/search" element={<SearchPage devModeNoAuth />} />
          <Route path="/app/profile" element={<ProfilePage devModeNoAuth />} />
          <Route path="/app/:module" element={<Dashboard devModeNoAuth />} />
          <Route path="/app/:module/sources" element={<Sources devModeNoAuth />} />
          <Route path="/app/:module/items" element={<Items devModeNoAuth />} />
          <Route path="/app/:module/items/:id" element={<ItemDetail devModeNoAuth />} />
          <Route path="/app/:module/entities" element={<Entities devModeNoAuth />} />
          <Route path="/app/:module/alerts" element={<Alerts devModeNoAuth />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route
        path="/sign-in/*"
        element={
          <AuthScreen>
            <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" fallbackRedirectUrl="/app" />
          </AuthScreen>
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <AuthScreen>
            <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" fallbackRedirectUrl="/app" />
          </AuthScreen>
        }
      />
      <Route
        path="/app/*"
        element={
          <RequireAuth>
            <Routes>
              <Route element={<Shell />}>
                <Route index element={<Overview />} />
                <Route path="admin/seed/:module" element={<ModuleSeed />} />
                <Route path="command" element={<CommandCenter />} />
                <Route path="intel" element={<IntelPage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path=":module" element={<Dashboard />} />
                <Route path=":module/sources" element={<Sources />} />
                <Route path=":module/items" element={<Items />} />
                <Route path=":module/items/:id" element={<ItemDetail />} />
                <Route path=":module/entities" element={<Entities />} />
                <Route path=":module/alerts" element={<Alerts />} />
              </Route>
            </Routes>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function AuthScreen({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "max(24px, env(safe-area-inset-top)) 16px max(24px, env(safe-area-inset-bottom))",
      }}
    >
      {children}
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  if (!isLoaded) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", color: "var(--bios-text-muted)", fontSize: 13 }}>
        Loading…
      </div>
    );
  }
  if (!isSignedIn) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/sign-in?redirect_url=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <>{children}</>;
}
